/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

/* eslint no-undef: 0 */

import axios from "axios";
import axiosRetry from 'axios-retry';
import {
  encryptToastCredentialFieldsInFormData,
  encryptToastCredentialFieldsInObject,
} from "../utils/toastCredentialRequestCrypto";
import {
  attachRequestIdToConfig,
  getRequestIdFromError,
  getRequestIdFromHeaders,
} from "../utils/requestCorrelation";
import { finalizeAuthenticatedApiError } from "./errorResponseHelpers";
import authManager from "../utils/authManager";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:3001/api";

export const buildUnauthorizedSilentError = (enrichedError) => {
  const silentError = new Error(enrichedError.message || "Unauthorized");

  silentError.response = enrichedError.response;
  silentError.config = enrichedError.config;
  silentError.requestId = enrichedError.requestId;

  return silentError;
};

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
});

const getRequestPathname = (url) => {
  if (!url) return "";

  try {
    return new URL(url, API_BASE_URL).pathname;
  } catch {
    return url.split("?")[0];
  }
};

const isRestaurantResourcePath = (pathname) => /^\/v1\/admin\/restaurants\/[^/]+$/.test(pathname);

export const shouldEncryptToastCredentials = (config) => {
  const method = (config?.method || "get").toLowerCase();
  const pathname = getRequestPathname(config?.url);

  if (pathname === "/v1/admin/restaurants" && ["post", "get"].includes(method)) return true;
  if (pathname === "/v1/admin/restaurants/fetch_restaurant_details" && method === "post") return true;

  return isRestaurantResourcePath(pathname) && ["get", "put", "patch"].includes(method);
};

const encryptToastCredentialRequest = (config) => {
  if (config?.data instanceof FormData) {
    encryptToastCredentialFieldsInFormData(config.data);
  } else if (config?.data && typeof config.data === "object") {
    config.data = encryptToastCredentialFieldsInObject(config.data);
  }

  if (config?.params && typeof config.params === "object") {
    config.params = encryptToastCredentialFieldsInObject(config.params);
  }

  return config;
};

axiosRetry(api, {
  retries: 3,
  retryDelay: (retryCount, error) => {
    return Math.min(axiosRetry.exponentialDelay(retryCount, error, 1000), 30000);
  },  
  retryCondition: (error) => {
    if (axiosRetry.isNetworkError(error)) {
      return true;
    }

    const status = error?.response?.status;
    return [502, 503, 504].includes(status);
  },
  onRetry: (retryCount, error) => {
    console.log(
      `Retry attempt #${retryCount} for ${error?.config?.url}`
    );
  }
});

api.interceptors.request.use(
  (config) => {
    const authToken = authManager.getAuthToken();

    if (authToken) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) {
        config.headers.Authorization = authToken;
      }
    }
    // Attach a correlation id to every outgoing API request.
    let updatedConfig = attachRequestIdToConfig(config);

    if (shouldEncryptToastCredentials(updatedConfig)) {
      updatedConfig = encryptToastCredentialRequest(updatedConfig);
    }

    return updatedConfig;
  },
  (error) => {
    return Promise.reject(error);
  }
);

api.interceptors.response.use(
  (response) => {
    const requestId =
      getRequestIdFromHeaders(response?.headers) ||
      response?.config?.requestId ||
      response?.config?.metadata?.requestId;

    if (requestId) {
      response.requestId = requestId;
      response.config = {
        ...response.config,
        requestId,
      };
    }

    return response;
  },
  (error) => {
    const requestId = getRequestIdFromError(error);

    return Promise.reject(
      finalizeAuthenticatedApiError(error, requestId, {
        buildUnauthorizedSilentError,
      }),
    );
  }
);

export default api;
